import api, { V2 } from './axios';

/** What a consultation booking records as its service, so a coupon scoped to
 *  this slug applies to consultations and nothing else. Mirrors the backend. */
export const CONSULTATION_SERVICE_SLUG = 'online-consultation';

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

/** One working window, "HH:MM" in IST. */
export interface AvailabilityWindow {
  start: string;
  end: string;
}

export type Availability = Record<Weekday, AvailabilityWindow[]>;

export interface Consultant {
  consultant_id: string;
  name: string;
  /** Admin responses only; never present in a public response. */
  email?: string;
  qualification?: string;
  bio?: string;
  photo_url?: string;
  languages?: string[];
  active?: boolean;
  availability?: Availability;
  blackout_dates?: string[];
  created_at?: string;
  updated_at?: string;
}

/** Refund by notice given: [minimum hours, fraction returned, tier label]. */
export type CancellationTier = [number, number, string];

export interface CancellationPolicy {
  tiers: CancellationTier[];
  grace_minutes: number;
  grace_min_lead_hours: number;
  free_reschedule_hours: number;
}

export interface ConsultationConfig {
  enabled: boolean;
  price: number;
  currency: string;
  session_minutes: number;
  notice_minutes: number;
  max_advance_days: number;
  coupons_enabled: boolean;
  title: string;
  blurb: string;
  default_availability: Availability;
  cancellation: CancellationPolicy;
}

export interface ConsultationSlot {
  time: string;
  display: string;
  available: number;
}

export interface OpenDay {
  date: string;
  slots: number;
  seats: number;
}

export interface ConsultationBooking {
  consultation_id: string;
  consultant_name: string;
  scheduled_date: string;
  scheduled_time: string;
  session_minutes: number;
  price: number;
  currency: string;
  total_amount: number;
  status: string;
  payment_status: string;
  meet_status?: string;
  meet_link?: string | null;
  notes?: string;
  cancel_reason?: string | null;
  created_at?: string;
  /** Decided by the API from policy and timing, so the UI never guesses. */
  can_reschedule?: boolean;
  rescheduled_at?: string | null;
  session_over?: boolean;
  can_review?: boolean;
  reviewed?: boolean;
}

export interface ConsultationReview {
  name: string;
  rating: number;
  text: string;
  date: string | null;
}

export interface ConsultationReviewsPage {
  reviews: ConsultationReview[];
  average: number | null;
  count: number;
  page: number;
  limit: number;
  has_more: boolean;
}

/** What cancelling would return, and whether a free move is still possible. */
export interface CancellationTerms {
  hours_before: number | null;
  tier: string;
  grace_applied: boolean;
  refund_fraction: number;
  refund_amount: number;
  cancellation_fee: number;
  free_reschedule_available: boolean;
  free_reschedule_hours: number;
}

/** Admin sees the internal fields a patient never does. */
export interface AdminConsultationBooking extends ConsultationBooking {
  consultant_id?: string;
  user_email?: string;
  user_name?: string;
  payment_id?: string;
  cancellation?: { tier: string; refund_amount: number };
}

export type ConsultantInput = Partial<Omit<Consultant, 'consultant_id'>>;

/** Public: what the booking page reads. */
export const consultationsApi = {
  getConfig: () =>
    api.get<{ consultation: Partial<ConsultationConfig> & { available: boolean; first_date: string; last_date: string } }>(
      `${V2}consultations/config/`,
    ),
  getConsultants: () => api.get<{ consultants: Consultant[] }>(`${V2}consultations/consultants/`),
  getSlots: (date: string) =>
    api.get<{ date: string; available: boolean; session_minutes: number; slots: ConsultationSlot[] }>(
      `${V2}consultations/availability/`,
      { params: { date } },
    ),
  getOpenDays: (from: string, to: string) =>
    api.get<{ available: boolean; days: OpenDay[] }>(`${V2}consultations/availability/`, {
      params: { from, to },
    }),

  /** Hold a slot. The Idempotency-Key makes a double tap safe to retry. */
  hold: (date: string, time: string, opts: { notes?: string; idempotencyKey: string }) =>
    api.post<{ message: string; consultation: ConsultationBooking }>(
      `${V2}consultations/bookings/`,
      { date, time, notes: opts.notes },
      { headers: { 'Idempotency-Key': opts.idempotencyKey } },
    ),
  myBookings: () =>
    api.get<{ consultations: ConsultationBooking[] }>(`${V2}consultations/bookings/`),
  getBooking: (id: string) =>
    api.get<{ consultation: ConsultationBooking }>(
      `${V2}consultations/bookings/${encodeURIComponent(id)}/`),
  cancelPreview: (id: string) =>
    api.get<{ terms: CancellationTerms }>(
      `${V2}consultations/bookings/${encodeURIComponent(id)}/cancel-preview/`),
  cancel: (id: string) =>
    api.post<{ message: string } & CancellationTerms>(
      `${V2}consultations/bookings/${encodeURIComponent(id)}/cancel/`, {}),
  reschedule: (id: string, date: string, time: string) =>
    api.post<{ message: string; consultation: ConsultationBooking }>(
      `${V2}consultations/bookings/${encodeURIComponent(id)}/reschedule/`, { date, time }),
  submitReview: (id: string, rating: number, review: string) =>
    api.post<{ message: string }>(
      `${V2}consultations/bookings/${encodeURIComponent(id)}/review/`, { rating, review }),
  /** Public. What patients said, newest first, with the overall figure. */
  reviews: (params: { page?: number; limit?: number } = {}) =>
    api.get<ConsultationReviewsPage>(`${V2}consultations/reviews/`, { params }),
};

/** Admin: manage consultants and the settings behind the feature. */
export const consultationsAdminApi = {
  getConfig: () =>
    api.get<{ config: ConsultationConfig; consultant_count: number; active_consultant_count: number }>(
      `${V2}consultations/admin/config/`,
    ),
  saveConfig: (data: Partial<ConsultationConfig>) =>
    api.put<{ message: string; config: ConsultationConfig }>(`${V2}consultations/admin/config/`, data),
  list: () => api.get<{ consultants: Consultant[] }>(`${V2}consultations/admin/consultants/`),
  create: (data: ConsultantInput) =>
    api.post<{ message: string; consultant: Consultant }>(`${V2}consultations/admin/consultants/`, data),
  update: (id: string, data: ConsultantInput) =>
    api.patch<{ message: string; consultant: Consultant }>(
      `${V2}consultations/admin/consultants/${encodeURIComponent(id)}/`,
      data,
    ),
  bookings: (params?: { status?: string; from?: string; to?: string; email?: string; limit?: number }) =>
    api.get<{ consultations: AdminConsultationBooking[]; count: number; total: number }>(
      `${V2}consultations/admin/bookings/`, { params }),
  remove: (id: string) =>
    api.delete<{ message: string }>(`${V2}consultations/admin/consultants/${encodeURIComponent(id)}/`),
};

/** A full week of empty days, so an editor always has every weekday to fill. */
export function emptyAvailability(): Availability {
  return WEEKDAYS.reduce((acc, day) => ({ ...acc, [day]: [] }), {} as Availability);
}
